import * as layout from '../markLayout.module.css';
import { MarkButton } from '../MarkControls';
import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import type { BlastVariantTarget, Channel, MarkTemplateRow, TemplateLoadState } from '../../types';
import useBlastDetail from '../../api/useBlastDetail';
import useMarkTemplate from '../../api/useMarkTemplate';
import TemplateEditorShell from './TemplateEditorShell';
type Props = {
    target: BlastVariantTarget;
    channel: Channel;
    readOnly?: boolean;
    targetKind?: string | null;
    workspace?: boolean;
};
type ResolvedTemplate = {
    sessionKey: string;
    templateId: number | null;
    row: MarkTemplateRow | null;
};
const BlastTemplateLoader: React.FC<Props> = ({
    target,
    channel,
    readOnly,
    targetKind,
    workspace
}) => {
    const sessionKey = `${target.blast_id}-${target.variant_label}-${channel}`;
    const [resolved, setResolved] = useState<ResolvedTemplate | null>(null);
    const frozen = resolved?.sessionKey === sessionKey ? resolved : null;
    const blastQuery = useBlastDetail(target.blast_id);
    const variant = blastQuery.data?.data?.variants.find((v) => v.label === target.variant_label);
    const templateId = variant?.template_id ?? null;
    const detailQuery = useMarkTemplate(frozen ? null : templateId);
    // Background refetch failures keep their data; only a failed first load is an error.
    const blastFailed = blastQuery.isError && !blastQuery.data;
    const detailFailed = detailQuery.isError && !detailQuery.data;
    const liveLoadState: TemplateLoadState = blastFailed
        ? 'error'
        : blastQuery.isPending
          ? 'loading'
          : templateId == null
            ? 'missing'
            : detailFailed
              ? 'error'
              : detailQuery.isPending
                ? 'loading'
                : detailQuery.data?.data?.channel !== channel
                  ? 'missing'
                  : 'existing';
    // Freeze the first resolution so later refetches (including the one a
    // create triggers) never remount the shell and drop unsaved edits.
    // Wait out the mount refetch of stale cached data: freezing an outdated row would
    // overwrite newer edits, and an outdated 'missing' would turn saves into creates.
    // A failed refetch leaves isFetching false, so stale data still freezes as a fallback.
    const settled =
        !blastQuery.isFetching && (liveLoadState !== 'existing' || !detailQuery.isFetching);
    const canFreeze = settled && (liveLoadState === 'existing' || liveLoadState === 'missing');
    if (!frozen && canFreeze) {
        const isExisting = liveLoadState === 'existing';
        setResolved({
            sessionKey,
            templateId: isExisting ? templateId : null,
            row: isExisting ? (detailQuery.data?.data ?? null) : null
        });
    }
    if (!frozen) {
        if (liveLoadState === 'error') {
            const failedQuery = blastFailed ? blastQuery : detailQuery;
            return (
                <div
                    role="alert"
                    style={{
                        backgroundColor: 'var(--mark-danger-surface)',
                        color: 'var(--mark-danger)'
                    }}
                    className={layout.notice}
                >
                    <P noDefaultMargin size="small">
                        Failed to load template data.
                    </P>
                    <MarkButton
                        type="button"
                        onClick={() => failedQuery.refetch()}
                        style={{
                            marginTop: '0.8rem'
                        }}
                    >
                        Retry
                    </MarkButton>
                </div>
            );
        }
        return <InlineLoader />;
    }
    return (
        <TemplateEditorShell
            key={sessionKey}
            target={target}
            channel={channel}
            markTemplate={frozen.row}
            templateLoadState={frozen.templateId != null ? 'existing' : 'missing'}
            templateId={frozen.templateId}
            readOnly={readOnly}
            targetKind={targetKind}
            workspace={workspace}
        />
    );
};
export default BlastTemplateLoader;
